/* eslint-disable jsdoc/require-returns */

/* eslint-disable jsdoc/check-param-names */
/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';
import superstyled from '@zenbusiness/zui/styles/superstyled';
import Text from '@zenbusiness/zui/Text';

/**
 * Highlight variants supported by TableSection.
 * - 'text': bold text only
 * - 'cell': cell background only
 * - 'both': bold text + cell background
 */
const HIGHLIGHT_VARIANTS = ['text', 'cell', 'both'];

/**
 * Normalizes a style config from several supported shapes.
 *
 * Supported inputs:
 * - boolean: true => { highlight: 'cell' }
 * - string: 'text' | 'cell' | 'both' => { highlight: <variant> }
 * - object: { bold?: boolean, highlight?: boolean|string, highlightVariant?: string }
 * @param input
 */
function normalizeStyleConfig(input) {
  if (!input) {
    return {};
  }

  if (input === true) {
    return { highlight: 'cell' };
  }

  if (typeof input === 'string') {
    return { highlight: input };
  }

  if (typeof input === 'object') {
    const highlightVariant = input.highlightVariant ?? input.highlight;

    let highlightValue;
    if (highlightVariant === true) {
      highlightValue = 'cell';
    } else if (typeof highlightVariant === 'string') {
      highlightValue = highlightVariant;
    } else {
      highlightValue = undefined;
    }

    return {
      bold: Boolean(input.bold),
      highlight: highlightValue
    };
  }

  return {};
}

function resolveFlags(...configs) {
  const merged = configs.reduce((acc, cfg) => ({ ...acc, ...normalizeStyleConfig(cfg) }), {});
  const highlight = HIGHLIGHT_VARIANTS.includes(merged.highlight) ? merged.highlight : undefined;

  // "Highlight text" is implemented as bold text.
  return {
    bold: Boolean(merged.bold) || highlight === 'text' || highlight === 'both',
    highlighted: highlight === 'cell' || highlight === 'both'
  };
}

function getIndexedConfig(mapLike, index) {
  if (!mapLike || typeof mapLike !== 'object') {
    return undefined;
  }

  // Supports { 0: {...}, 1: {...} } where keys are strings in JSON.
  return mapLike[index] ?? mapLike[String(index)];
}

function getCellConfig(cellStyles, rowIndex, colIndex) {
  const rowCfg = getIndexedConfig(cellStyles, rowIndex);
  return getIndexedConfig(rowCfg, colIndex);
}

function getCellValue(cell) {
  if (cell && typeof cell === 'object' && Object.prototype.hasOwnProperty.call(cell, 'value')) {
    return cell.value;
  }

  return cell;
}

/**
 * Styled Components
 */
const TableWrapper = superstyled({
  name: 'TableSection',
  slot: 'Wrapper',
  tag: Box,
  styles: ({ theme }) => ({
    width: '100%',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.rounded.md,
    backgroundColor: theme.palette.background.paper
  })
});

const StyledTable = superstyled({
  name: 'TableSection',
  slot: 'Table',
  tag: 'table',
  styles: () => ({
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  })
});

const TableHead = superstyled({
  name: 'TableSection',
  slot: 'Head',
  tag: 'thead',
  styles: ({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    borderBottom: `2px solid ${theme.palette.border.main}`
  })
});

const TableHeaderCell = superstyled({
  name: 'TableSection',
  slot: 'HeaderCell',
  tag: 'th',
  propsNeeded: ['highlighted', 'bold'],
  styles: ({ theme, highlighted = false, bold = false }) => ({
    padding: theme.spacing(2),
    textAlign: 'left',
    fontWeight: '500',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    ...(bold && {
      fontWeight: '600'
    }),
    ...(highlighted && {
      backgroundColor: '#D1FDFF'
    })
  })
});

const TableBody = superstyled({
  name: 'TableSection',
  slot: 'Body',
  tag: 'tbody',
  styles: () => ({})
});

const TableRow = superstyled({
  name: 'TableSection',
  slot: 'Row',
  tag: 'tr',
  styles: ({ theme, striped }) => ({
    borderBottom: `1px solid ${theme.palette.border.light}`,
    ...(striped && {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.background.default
      }
    }),
    '&:last-child': {
      borderBottom: 'none'
    }
  })
});

const TableCell = superstyled({
  name: 'TableSection',
  slot: 'Cell',
  tag: 'td',
  propsNeeded: ['highlighted', 'bold'],
  styles: ({ theme, highlighted = false, bold = false }) => ({
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    verticalAlign: 'top',
    ...(bold && {
      fontWeight: '600',
      color: theme.palette.text.primary
    }),
    ...(highlighted && {
      backgroundColor: '#E1FDFF'
    })
  })
});

const Caption = superstyled({
  name: 'TableSection',
  slot: 'Caption',
  tag: Box,
  styles: ({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.border.main}`,
    backgroundColor: theme.palette.background.default
  })
});

/**
 * TableSection Component
 *
 * Displays data in a formatted table within report sections.
 * Supports responsive design with horizontal scroll on mobile.
 *
 * @param {object} props - Component props
 * @param {object} props.content - Table data
 * @param {string[]} props.content.headers - Array of column header labels
 * @param {Array<Array<string|number|object|React.ReactNode>>} props.content.rows - Array of row arrays (each row is array of cell values)
 * @param {string} [props.content.caption] - Optional caption text displayed below table
 * @param {boolean} [props.content.striped=true] - Whether to stripe alternate rows
 * @param {object} [props.content.columnStyles] - Per-column style overrides (keyed by column index)
 * @param {object} [props.content.rowStyles] - Per-row style overrides (keyed by visual row index)
 * @param {object} [props.content.cellStyles] - Per-cell style overrides (keyed by visual row index, then column index)
 *
 * Visual row indexing:
 * - row 0 maps to the header row (`headers`)
 * - row 1 maps to `rows[0]`, row 2 maps to `rows[1]`, etc.
 *
 * Style config values support:
 * - `true` (equivalent to `{ highlight: 'cell' }`)
 * - `'text' | 'cell' | 'both'`
 * - `{ bold?: boolean, highlight?: boolean|'text'|'cell'|'both', highlightVariant?: 'text'|'cell'|'both' }`
 *
 * Per-cell values can also be objects: `{ value: <any>, bold?: boolean, highlight?: ... }`
 *
 * @example
 * <TableSection content={{
 *   headers: ['Segment', 'Market Share', 'Growth Rate'],
 *   rows: [
 *     ['Enterprise', '42%', '+12%'],
 *     ['SMB', '35%', '+8%']
 *   ],
 *   caption: 'Customer Segmentation Breakdown',
 *   striped: true,
 *   columnStyles: { 1: 'cell' }
 * }} />
 */
function TableSection({ content }) {
  // const theme = useTheme();

  if (!content || !content.headers || !content.rows) {
    return null;
  }

  const { headers, rows, caption, striped = true, columnStyles, rowStyles, cellStyles } = content;

  return (
    <TableWrapper>
      <StyledTable>
        <TableHead>
          <TableRow>
            {headers.map((header, colIndex) => {
              const columnCfg = getIndexedConfig(columnStyles, colIndex);
              const headerRowCfg = getIndexedConfig(rowStyles, 0);
              const headerCellCfg = getCellConfig(cellStyles, 0, colIndex);
              const inlineCfg = header && typeof header === 'object' ? header : null;

              const { bold, highlighted } = resolveFlags(
                columnCfg,
                headerRowCfg,
                headerCellCfg,
                inlineCfg
              );

              return (
                <TableHeaderCell key={colIndex} bold={bold} highlighted={highlighted}>
                  {getCellValue(header)}
                </TableHeaderCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => {
            // Visual row index: row 0 = header, row 1 = first body row.
            const visualRowIndex = rowIndex + 1;
            const rowCfg = getIndexedConfig(rowStyles, visualRowIndex);

            return (
              <TableRow key={rowIndex} striped={striped}>
                {row.map((cell, colIndex) => {
                  const columnCfg = getIndexedConfig(columnStyles, colIndex);
                  const cellCfg = getCellConfig(cellStyles, visualRowIndex, colIndex);
                  const inlineCfg = cell && typeof cell === 'object' ? cell : null;

                  const { bold, highlighted } = resolveFlags(columnCfg, rowCfg, cellCfg, inlineCfg);

                  return (
                    <TableCell key={colIndex} bold={bold} highlighted={highlighted}>
                      {getCellValue(cell)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </StyledTable>
      {caption && (
        <Caption>
          <Text color="secondary" size="sm">
            {caption}
          </Text>
        </Caption>
      )}
    </TableWrapper>
  );
}

TableSection.propTypes = {
  content: PropTypes.shape({
    headers: PropTypes.arrayOf(PropTypes.any).isRequired,
    rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.any)).isRequired,
    caption: PropTypes.string,
    striped: PropTypes.bool,

    // Style overrides
    columnStyles: PropTypes.object, // { [colIndex]: styleConfig }
    rowStyles: PropTypes.object, // { [rowIndex]: styleConfig }
    cellStyles: PropTypes.object // { [rowIndex]: { [colIndex]: styleConfig } }
  }).isRequired
};

export default TableSection;
